import React, { useLayoutEffect, useRef, useState } from "react";
import { Menu, MenuItem } from "@mui/material";
import {
  chipBesideCaret,
  fillChipEditor,
  insertChipAtRange,
  insertLineBreakAtRange,
  serializeChipEditor,
} from "./emailSettingsDisplay";

const rangeInside = (root, range) => {
  if (!root || !range) return false;
  try {
    return (
      root.contains(range.startContainer) && root.contains(range.endContainer)
    );
  } catch {
    return false;
  }
};

export const VariableTextField = ({
  label,
  required,
  value,
  onChange,
  variables = [],
  max,
  multiline = false,
}) => {
  const editorRef = useRef(null);
  const selectionRef = useRef(null);
  const emittedRef = useRef(value ?? "");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const labelFor = (key) =>
    variables.find((item) => item.key === key)?.label || key;

  useLayoutEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    if (editor.dataset.hydrated === "1" && value === emittedRef.current) return;
    fillChipEditor(editor, value, labelFor);
    emittedRef.current = value ?? "";
    editor.dataset.hydrated = "1";
  }, [value, variables]); // eslint-disable-line react-hooks/exhaustive-deps

  const rememberSelection = () => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (!rangeInside(editor, range)) return;
    selectionRef.current = range.cloneRange();
  };

  const placeCaret = (range) => {
    const selection = window.getSelection();
    if (!selection || !range) return;
    selection.removeAllRanges();
    selection.addRange(range);
    selectionRef.current = range.cloneRange();
  };

  const commitFromDom = () => {
    const editor = editorRef.current;
    if (!editor) return;
    let next = serializeChipEditor(editor);
    if (!multiline) next = next.replace(/\n/g, "");
    if (max && next.length > max) {
      fillChipEditor(editor, emittedRef.current, labelFor);
      return;
    }
    if (next === emittedRef.current) return;
    emittedRef.current = next;
    onChange(next);
  };

  const insertAtCaret = (key) => {
    const editor = editorRef.current;
    if (!editor) return;
    const token = `{{${key}}}`;
    const current = serializeChipEditor(editor);
    if (max && current.length + token.length > max) return;
    editor.focus();
    const range = rangeInside(editor, selectionRef.current)
      ? selectionRef.current
      : null;
    const caret = insertChipAtRange(editor, range, key, labelFor(key));
    placeCaret(caret);
    commitFromDom();
  };

  return (
    <div className="email-settings-field">
      <span>
        {label}
        {required ? <em>*</em> : null}
      </span>
      <div
        className={`email-chip-field${multiline ? " email-chip-field--multiline" : ""}`}
      >
        <div
          ref={editorRef}
          className="email-chip-field__body"
          contentEditable
          role="textbox"
          aria-multiline={multiline}
          spellCheck
          onMouseUp={rememberSelection}
          onKeyUp={rememberSelection}
          onBlur={rememberSelection}
          onInput={commitFromDom}
          onPaste={(event) => {
            event.preventDefault();
            const editor = editorRef.current;
            const selection = window.getSelection();
            if (!editor || !selection?.rangeCount) return;
            const range = selection.getRangeAt(0);
            if (!rangeInside(editor, range)) return;
            let text = event.clipboardData?.getData("text/plain") || "";
            if (!multiline) text = text.replace(/[\r\n]+/g, " ");
            range.deleteContents();
            const node = document.createTextNode(text);
            range.insertNode(node);
            const after = document.createRange();
            after.setStart(node, node.textContent.length);
            after.collapse(true);
            placeCaret(after);
            commitFromDom();
          }}
          onMouseDown={(event) => {
            if (event.target.closest?.("[data-remove-var]")) {
              event.preventDefault();
            }
          }}
          onClick={(event) => {
            const remove = event.target.closest?.("[data-remove-var]");
            const editor = editorRef.current;
            if (!remove || !editor?.contains(remove)) return;
            event.preventDefault();
            remove.closest("[data-var]")?.remove();
            commitFromDom();
            editor.focus();
          }}
          onKeyDown={(event) => {
            if (!multiline && event.key === "Enter") {
              event.preventDefault();
              return;
            }
            if (multiline && event.key === "Enter") {
              event.preventDefault();
              const editor = editorRef.current;
              const selection = window.getSelection();
              if (!editor || !selection?.rangeCount) return;
              const range = selection.getRangeAt(0);
              if (!rangeInside(editor, range)) return;
              placeCaret(insertLineBreakAtRange(editor, range));
              commitFromDom();
              return;
            }
            if (event.key !== "Backspace" && event.key !== "Delete") return;
            const editor = editorRef.current;
            const selection = window.getSelection();
            if (!editor || !selection?.rangeCount) return;
            const range = selection.getRangeAt(0);
            if (!range.collapsed || !rangeInside(editor, range)) return;
            const chip = chipBesideCaret(
              editor,
              range,
              event.key === "Backspace" ? "back" : "forward"
            );
            if (!chip) return;
            event.preventDefault();
            chip.remove();
            commitFromDom();
          }}
        />
        <button
          type="button"
          className="email-chip-field__add"
          onMouseDown={(event) => {
            event.preventDefault();
            rememberSelection();
          }}
          onClick={(event) => setMenuAnchor(event.currentTarget)}
        >
          + Add Variable
        </button>
      </div>
      {max ? (
        <em>
          Character limit: {String(value || "").length}/{max}
        </em>
      ) : null}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        {variables.map((item) => (
          <MenuItem
            key={item.key}
            onClick={() => {
              insertAtCaret(item.key);
              setMenuAnchor(null);
            }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};
